import { Stack } from "../../../stack";
import { useInteractionModalContext } from "../hooks";
import { useScrollPositionPreservation } from "../hooks";
import { SidebarItemType } from "./sidebar-content.types";
import { usePrevious } from "@mantine/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "../../../box";
import styles from "./sidebar-content.module.scss";
import { Item } from "./sidebar-item";
import { FetchMoreItems } from "./sidebar-fetch-more-items";

/**
 * Renders the sidebar showing a scrollable list of interactions from the same contact with pagination.
 * @returns {JSX.Element | null} The SidebarContent component, or null if no contact interactions data exists.
 */
export const SidebarContent = () => {
    const { interaction, interactionsFromSameContact, interactionsFromSameContactNavigation } =
        useInteractionModalContext();

    const previousItemsList = usePrevious(interactionsFromSameContact?.data);
    const activeItemRef = useRef<HTMLDivElement>(null);
    const [shouldScrollToItem, setShouldScrollToItem] = useState(true);
    const isLoading = useMemo(
        () => interactionsFromSameContact?.isLoading || interaction.isLoading,
        [interactionsFromSameContact, interaction]
    );

    const [scrollRef, { enable: enableScrollPreservation }] = useScrollPositionPreservation(
        interactionsFromSameContact?.data?.length !== previousItemsList?.length
    );

    // Auto-scroll to active item when the data loads
    useEffect(() => {
        if (shouldScrollToItem && !isLoading && interaction.data && activeItemRef.current) {
            activeItemRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            setShouldScrollToItem(false);
        }
    }, [shouldScrollToItem, interaction.data, isLoading]);

    useEffect(() => {
        if (interactionsFromSameContact?.isLoading) {
            setShouldScrollToItem(true);
        }
    }, [interactionsFromSameContact?.isLoading]);

    if (typeof interactionsFromSameContact === "undefined") {
        return null;
    }

    return (
        <Stack ref={scrollRef} className={styles["sidebar-content"]} align="flex-start" justify="flex-start">
            <Stack w="100%" gap="sm" align="flex-start" justify="flex-start">
                {interactionsFromSameContact.isLoading && (
                    <>
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                    </>
                )}
                {interactionsFromSameContact.hasNextPage && !interactionsFromSameContact.isLoading && (
                    <FetchMoreItems
                        label="See Newer"
                        type={SidebarItemType.Newer}
                        onClick={() => {
                            enableScrollPreservation();
                            interactionsFromSameContact?.fetchNextPage?.();
                        }}
                        isLoading={interactionsFromSameContact.isLoadingNextPage}
                    />
                )}
                {!interactionsFromSameContact.isLoading &&
                    interactionsFromSameContact.data?.map((item) => {
                        const isActive = interaction.data?.interactionId === item.interactionId;
                        return (
                            <Box key={item.interactionId} ref={isActive ? activeItemRef : null} w="100%">
                                <Item
                                    onClick={() => interactionsFromSameContactNavigation?.onClick(item.interactionId)}
                                    active={isActive}
                                    interactionType={item.interactionType}
                                    dateTime={item.startDateTime}
                                />
                            </Box>
                        );
                    })}
                {interactionsFromSameContact.hasPreviousPage && !interactionsFromSameContact.isLoading && (
                    <FetchMoreItems
                        label="See Older"
                        type={SidebarItemType.Older}
                        onClick={() => {
                            interactionsFromSameContact?.fetchPreviousPage?.();
                        }}
                        isLoading={interactionsFromSameContact.isLoadingPreviousPage}
                    />
                )}
            </Stack>
        </Stack>
    );
};
